import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Layout from '../../components/ui/Layout';
import Breadcrumb from '../../components/ui/Breadcrumb';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import Icon from '../../components/AppIcon';

import VerificationQueue from './components/VerificationQueue';
import PerformanceAnalytics from './components/PerformanceAnalytics';
import KYCReview from './components/KYCReview';
import FilterToolbar from './components/FilterToolbar';

const PartnerAnalyticsHub = () => {
  const [activeTab, setActiveTab] = useState('verification');
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);

  const tabs = [
    {
      id: 'verification',
      label: 'Verification Queue',
      icon: 'UserCheck',
      description: 'Partner verification workflow management',
      count: 23
    },
    {
      id: 'performance',
      label: 'Performance Analytics',
      icon: 'BarChart3',
      description: 'Partner performance metrics and rankings',
      count: null
    },
    {
      id: 'kyc',
      label: 'KYC Review',
      icon: 'FileText',
      description: 'Document review and approval workflow',
      count: 12
    }
  ];

  const quickActions = [
    {
      id: 'bulk-approve',
      label: 'Bulk Approve',
      icon: 'CheckCircle',
      onClick: () => console.log('Bulk approve'),
      variant: 'success'
    },
    {
      id: 'send-reminder',
      label: 'Send Reminder',
      icon: 'Mail',
      onClick: () => console.log('Send reminder'),
      variant: 'outline'
    }
  ];

  useEffect(() => {
    // Simulate data refresh every 30 minutes
    const interval = setInterval(() => {
      setLastRefresh(new Date());
    }, 30 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const handleRefresh = async () => {
    setIsLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setLastRefresh(new Date());
    setIsLoading(false);
  };

  const handleExport = async () => {
    // Simulate export functionality
    console.log('Exporting partner analytics data...');
  };

  const handleFiltersChange = (filters) => {
    console.log('Filters changed:', filters);
    // In a real app, this would trigger data refetch
  };

  const handleBulkAction = (actionId, selectedItems) => {
    // console.log('Bulk action:', actionId, selectedItems);
    // In a real app, this would perform the bulk operation
  };

  const formatLastRefresh = () => {
    const now = new Date();
    const diffInMinutes = Math.floor((now - lastRefresh) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    return `${Math.floor(diffInMinutes / 60)}h ago`;
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'verification':
        return <VerificationQueue />;
      case 'performance':
        return <PerformanceAnalytics />;
      case 'kyc':
        return <KYCReview />;
      default:
        return <VerificationQueue />;
    }
  };

  return (
    <>
      <Helmet>
        <title>Partner Analytics Hub - Qkly Admin Panel</title>
        <meta name="description" content="Monitor partner verification workflows and performance metrics across all marketplace verticals" />
      </Helmet>
      <Layout>
        <div className="px-6 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <Breadcrumb className="mb-4" />
            
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">Partner Analytics Hub</h1>
                <p className="text-muted-foreground">
                  Monitor partner verification workflows and performance metrics across all marketplace verticals
                </p>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="text-sm text-muted-foreground">
                  Last updated: {formatLastRefresh()}
                </div>
                <QuickActionToolbar
                  actions={quickActions}
                  onRefresh={handleRefresh}
                  onExport={handleExport}
                  onFilter={() => {}}
                  showFilter={false}
                />
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="mb-6">
            <div className="border-b border-border">
              <nav className="flex space-x-8 overflow-x-auto">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                    }`}
                  >
                    <Icon name={tab?.icon} size={20} />
                    <span>{tab?.label}</span>
                    {tab?.count && (
                      <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full">
                        {tab?.count}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Description */}
            <div className="mt-4 p-4 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground">
                {tabs?.find(tab => tab?.id === activeTab)?.description}
              </p>
            </div>
          </div>

          {/* Filter Toolbar */}
          <div className="mb-6">
            <FilterToolbar 
              onFiltersChange={handleFiltersChange}
              onBulkAction={handleBulkAction}
            />
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex items-center space-x-3">
                  <Icon name="Loader2" size={24} className="animate-spin text-primary" />
                  <span className="text-muted-foreground">Loading partner data...</span>
                </div>
              </div>
            ) : (
              renderTabContent()
            )}
          </div>

          {/* Quick Stats Footer */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-card border border-border rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-foreground">1,247</div>
              <div className="text-sm text-muted-foreground">Total Partners</div>
              <div className="text-xs text-success mt-1">+12% this month</div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-foreground">96.2%</div>
              <div className="text-sm text-muted-foreground">Avg Completion Rate</div>
              <div className="text-xs text-success mt-1">+2.1% this month</div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-foreground">4.7</div>
              <div className="text-sm text-muted-foreground">Avg Partner Rating</div>
              <div className="text-xs text-success mt-1">+0.3 this month</div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-foreground">$2.4M</div>
              <div className="text-sm text-muted-foreground">Monthly Revenue</div>
              <div className="text-xs text-success mt-1">+18% this month</div>
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
};

export default PartnerAnalyticsHub;